import { IconButton } from '@creditornot/cb-button';
import { Menu, Trashbin } from '@creditornot/cb-icons';
import { Input, InputProps } from '@creditornot/cb-input';
import React, { ClipboardEvent, KeyboardEvent, useCallback, forwardRef } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';

type CategoryInputFieldProps = {
  canDelete: boolean;
  fieldName: string;
  placeholder: string;
  onBlur: () => void;
  touched: boolean;
  onPressEnter: () => void;
  onRemove: () => void;
  onPaste: (data: string[]) => void;
};

type CategoryInputProps = InputProps & {
  onDeleteClick?: () => void;
};

const Root = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  margin-inline-start: -32px;
`;

const CategoryInput = forwardRef<HTMLInputElement, CategoryInputProps>(
  ({ className, onDeleteClick, ...props }, ref) => (
    <Root data-testid="CategoryInput" className={className}>
      <Input icon={<Menu />} stretch {...props} inputRef={ref} />
      {onDeleteClick && (
        <StyledIconButton size="tiny" variant="grey" background="animated" onClick={onDeleteClick}>
          <Trashbin />
        </StyledIconButton>
      )}
    </Root>
  ),
);

export const CategoryInputField = ({
  fieldName,
  canDelete,
  placeholder,
  onBlur,
  touched,
  onPressEnter,
  onRemove,
  onPaste,
}: CategoryInputFieldProps) => {
  const field = useField<string>(fieldName);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const { valid } = field.meta;
      const pressedEnter = e.key === 'Enter';

      if (pressedEnter) {
        field.input.onBlur();
        onBlur();
      }

      if (pressedEnter && valid) {
        onPressEnter();
      }
    },
    [field.meta, field.input, onBlur, onPressEnter],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();

      const data = e.clipboardData
        .getData('text')
        .split('\n')
        .filter(v => v !== '');

      onPaste(data);
    },
    [onPaste],
  );

  return (
    <RFFFieldErrorWrapper
      field={field}
      showError={meta => (!meta.dirtySinceLastSubmit && !!meta.submitError) || touched}
    >
      {rffFieldErrorWrapperProps => (
        <CategoryInput
          {...rffFieldErrorWrapperProps}
          onBlur={e => {
            rffFieldErrorWrapperProps.onBlur(e);
            onBlur();
          }}
          onPaste={handlePaste}
          autoFocus
          onDeleteClick={canDelete ? onRemove : undefined}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
        />
      )}
    </RFFFieldErrorWrapper>
  );
};
